import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiData, registerUnauthorizedHandler, tokenStore } from '@shared/api/client';
import { ApiError } from '@shared/api/envelope';

export type UserRole = 'hajj' | 'umrah' | 'volunteer' | 'operations_admin' | 'super_admin';

export interface AuthenticatedUser {
  id: string;
  phone: string;
  name: string | null;
  email?: string | null;
  // iter-23: profile fields the combined /profile/complete screen captures.
  // Gender is intentionally separate from role.
  age?: number | null;
  gender?: 'male' | 'female' | null;
  role: UserRole | null;
  onboarding_state: 'language' | 'profile' | 'role' | 'health' | 'complete';
  language: 'ar' | 'en' | 'ur';
  volunteer_verification: 'pending' | 'approved' | 'rejected' | 'suspended' | null;
}

export interface AuthState {
  user: AuthenticatedUser | null;
  token: string | null;
  status: 'loading' | 'unauthenticated' | 'authenticated';
}

interface AuthContextValue extends AuthState {
  setSession(token: string, user: AuthenticatedUser): void;
  setUser(user: AuthenticatedUser): void;
  logout(): void;
}

const STORAGE_KEY = 'yusr.auth.token';

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(STORAGE_KEY, value);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore storage errors (private mode, etc.) */
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>(() => {
    const stored = readStoredToken();
    tokenStore.set(stored);
    return {
      user: null,
      token: stored,
      status: stored ? 'loading' : 'unauthenticated',
    };
  });

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      writeStoredToken(null);
      tokenStore.clear();
      setState({ user: null, token: null, status: 'unauthenticated' });
    });
    return () => registerUnauthorizedHandler(null);
  }, []);

  // On mount with a stored token, hydrate the user from /api/auth/me.
  // Prevents the "stuck on loading after refresh" bug Codex flagged in iter-1.
  useEffect(() => {
    if (state.status !== 'loading' || !state.token) return;
    let cancelled = false;
    apiData<AuthenticatedUser>({ method: 'GET', url: '/auth/me' })
      .then((user) => {
        if (cancelled) return;
        setState({ token: state.token, user, status: 'authenticated' });
      })
      .catch((err) => {
        if (cancelled) return;
        // 401 already cleared the token store via the registered unauth handler.
        if (err instanceof ApiError && err.isUnauthenticated()) return;
        // Network/5xx: don't blow away the session — surface a soft state instead.
        // We resolve to 'authenticated' with a null user so the route guards can
        // fall through to the unauthenticated branch on missing user, while a
        // future retry (in feature hooks) reloads /auth/me cleanly.
        setState((prev) =>
          prev.status === 'loading' ? { ...prev, status: 'unauthenticated' } : prev,
        );
      });
    return () => { cancelled = true; };
  }, [state.status, state.token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      setSession(token, user) {
        writeStoredToken(token);
        tokenStore.set(token);
        setState({ token, user, status: 'authenticated' });
      },
      setUser(user) {
        setState((prev) =>
          prev.token ? { token: prev.token, user, status: 'authenticated' } : prev,
        );
      },
      logout() {
        writeStoredToken(null);
        tokenStore.clear();
        setState({ user: null, token: null, status: 'unauthenticated' });
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
